/**
 * ProfileEditor — standalone client component for creating / editing a
 * MappingProfile.
 *
 * No imports from the mapper page (that file is actively being modified by
 * another agent). Inspector + sidebar are self-contained here.
 *
 * Layout mirrors the mapper UI shell (see docs/V2-PLAN.md §5) but the
 * center pane is replaced by a "Sample source row" JSON textarea so the editor
 * can be used in isolation — no workbook required.
 */

import { useCallback, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";
import {
  createProfile,
  deleteProfile,
  updateProfile,
} from "@/lib/api/endpoints";
import { COLUMN_GROUPS } from "@/lib/mapper/columns";
import { PRODUCT_TYPES } from "@/lib/mapper/types";
import { segmentsToEditing } from "@/modules/_shared/fieldInputText";
import { cellModule } from "@/lib/mapper/modules/catalog";
import type {
  Profile,
  Rule,
  SourceRow,
  VariantConfig,
  VariantOption,
} from "@/lib/mapper/types";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const TEMPLATE_HELPER_CHIPS = [
  "upper",
  "lower",
  "title",
  "trim",
  "slugify",
  "numberOnly",
  'default:"--"',
] as const;

const COMPUTED_FNS = [
  "slugify",
  "upper",
  "lower",
  "title",
  "trim",
  "numberOnly",
  "concat",
  "join",
  "addPrice",
  "default",
] as const;

type RuleKind = Rule["kind"];

const KINDS: { value: RuleKind; label: string }[] = [
  { value: "source", label: "source" },
  { value: "static", label: "static" },
  { value: "template", label: "template" },
  { value: "computed", label: "computed" },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function emptyRule(kind: RuleKind): Rule {
  switch (kind) {
    case "source":
      return { kind: "source", column: "" };
    case "static":
      return { kind: "static", value: "" };
    case "template":
      return { kind: "template", value: "" };
    case "computed":
      return { kind: "computed", fn: "slugify", args: [] };
    case "dictionary":
      return { kind: "dictionary", input: "", dictionary: "", onMiss: "passthrough" };
    case "dropbox-image":
      return { kind: "dropbox-image", input: "" };
    case "expression":
      return { kind: "expression", expr: "" };
    case "find-replace":
      return { kind: "find-replace", input: "", rules: [{ find: "", replace: "" }] };
    case "conditional":
      return {
        kind: "conditional",
        branches: [
          {
            when: { all: [{ column: "", op: "equals", value: "" }] },
            then: { kind: "static", value: "" },
          },
        ],
      };
    case "ai":
      return { kind: "ai", provider: "anthropic", model: "claude-sonnet-4-6", prompt: "" };
    default:
      // New / future module kinds provide their own defaults via the registry.
      return cellModule(kind)?.defaultConfig() ?? { kind: "static", value: "" };
  }
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export type ProfileEditorProps = {
  /** "new" means we're creating a fresh profile. */
  mode: "new" | "edit";
  profileId: string | null;
  /** Pre-loaded profile data (null for "new"). */
  initialName: string;
  initialProfile: Profile;
};

// ---------------------------------------------------------------------------
// ProfileEditor
// ---------------------------------------------------------------------------

export function ProfileEditor({
  mode,
  profileId,
  initialName,
  initialProfile,
}: ProfileEditorProps) {
  const navigate = useNavigate();

  // ---- meta state -----------------------------------------------------------
  const [profileName, setProfileName] = useState(initialName);
  const [profile, setProfile] = useState<Profile>(initialProfile);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // ---- inspector state ------------------------------------------------------
  const [selectedColumn, setSelectedColumn] = useState<string | null>(null);
  const [kindMemory, setKindMemory] = useState<Record<string, Rule>>({});
  const [variantsOpen, setVariantsOpen] = useState(false);

  // ---- source row / preview state -------------------------------------------
  const [sampleJson, setSampleJson] = useState<string>("");
  const [sampleParseError, setSampleParseError] = useState<string | null>(null);
  const [parsedRow, setParsedRow] = useState<SourceRow | null>(null);

  // ---- focused-input tracking for "click header inserts {{...}}" -----------
  const [focusedInputId, setFocusedInputId] = useState<string | null>(null);
  const inputRefs = useRef<Record<string, HTMLInputElement | HTMLTextAreaElement | null>>({});

  const registerInput = useCallback(
    (id: string) => (el: HTMLInputElement | HTMLTextAreaElement | null) => {
      inputRefs.current[id] = el;
    },
    [],
  );

  const insertAtFocus = useCallback(
    (text: string) => {
      if (!focusedInputId || !selectedColumn) return;
      const el = inputRefs.current[focusedInputId];
      if (!el) return;
      const start = el.selectionStart ?? el.value.length;
      const end = el.selectionEnd ?? el.value.length;
      const next = el.value.slice(0, start) + text + el.value.slice(end);
      const [, field] = focusedInputId.split("::");
      updateRuleField(field, next);
      requestAnimationFrame(() => {
        const after = inputRefs.current[focusedInputId];
        if (!after) return;
        const caret = start + text.length;
        after.focus();
        after.setSelectionRange(caret, caret);
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [focusedInputId, selectedColumn],
  );

  // ---- rule mutation ---------------------------------------------------------
  const currentRule: Rule | undefined = selectedColumn
    ? profile.fields[selectedColumn]
    : undefined;

  function setRule(next: Rule) {
    if (!selectedColumn) return;
    setProfile((p) => ({
      ...p,
      fields: { ...p.fields, [selectedColumn]: next },
    }));
    setKindMemory((m) => ({ ...m, [`${selectedColumn}::${next.kind}`]: next }));
    setDirty(true);
  }

  function updateRuleField(field: string, value: string) {
    if (!selectedColumn || !currentRule) return;
    const r = currentRule;
    if (field === "column" && r.kind === "source") {
      setRule({ ...r, column: value });
    } else if (field === "value" && (r.kind === "static" || r.kind === "template")) {
      setRule({ ...r, value });
    } else if (field === "fn" && r.kind === "computed") {
      setRule({ ...r, fn: value });
    } else if (field.startsWith("arg:") && r.kind === "computed") {
      const idx = Number(field.slice(4));
      const args = [...(r.args ?? [])];
      args[idx] = value;
      setRule({ ...r, args });
    }
  }

  function changeKind(kind: RuleKind) {
    if (!selectedColumn) return;
    const memKey = `${selectedColumn}::${kind}`;
    const remembered = kindMemory[memKey];
    if (remembered) {
      setRule(remembered);
    } else {
      setRule(emptyRule(kind));
    }
  }

  function clearRule() {
    if (!selectedColumn) return;
    setProfile((p) => {
      const fields = { ...p.fields };
      delete fields[selectedColumn];
      return { ...p, fields };
    });
    setDirty(true);
  }

  // ---- variants helpers ------------------------------------------------------
  const variants: VariantConfig = profile.variants ?? {
    enabled: false,
    optionName: "",
    options: [],
  };
  const variantOptions = variants.options ?? [];

  function updateVariants(next: Partial<VariantConfig>) {
    setProfile((p) => ({
      ...p,
      variants: { ...(p.variants ?? variants), ...next },
    }));
    setDirty(true);
  }

  function updateVariantOption(idx: number, patch: Partial<VariantOption>) {
    const options = [...variantOptions];
    options[idx] = { ...options[idx], ...patch };
    updateVariants({ options });
  }

  function addVariantOption() {
    updateVariants({
      options: [...variantOptions, { value: "", priceDelta: 0, skuSuffix: "" }],
    });
  }

  function removeVariantOption(idx: number) {
    updateVariants({ options: variantOptions.filter((_, i) => i !== idx) });
  }

  // ---- source row parsing ----------------------------------------------------
  const sampleDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Headers derived from the parsed sample row
  const sampleHeaders = useMemo<string[]>(
    () => (parsedRow ? Object.keys(parsedRow) : []),
    [parsedRow],
  );

  // Wrap setProfile so call sites stay unchanged
  function setProfileAndPreview(next: Profile) {
    setProfile(next);
  }

  // Also update parsed row
  function handleParsedRowChange(row: SourceRow | null) {
    setParsedRow(row);
  }

  function handleSampleChangeFull(raw: string) {
    setSampleJson(raw);
    if (sampleDebounceRef.current) clearTimeout(sampleDebounceRef.current);
    sampleDebounceRef.current = setTimeout(() => {
      if (!raw.trim()) {
        handleParsedRowChange(null);
        setSampleParseError(null);
        return;
      }
      try {
        const parsed = JSON.parse(raw);
        if (typeof parsed !== "object" || Array.isArray(parsed) || parsed === null) {
          setSampleParseError('Must be a JSON object like { "SKU": "60968", ... }');
          handleParsedRowChange(null);
          return;
        }
        setSampleParseError(null);
        handleParsedRowChange(parsed as SourceRow);
      } catch {
        setSampleParseError("Invalid JSON");
        handleParsedRowChange(null);
      }
    }, 300);
  }

  // ---- save / delete --------------------------------------------------------
  async function handleSave() {
    setSaving(true);
    setSaveError(null);
    try {
      const body = {
        name: profileName,
        productType: profile.productType,
        config: profile,
        isDefault: false,
      };
      if (mode === "new") {
        const created = await createProfile(body);
        setDirty(false);
        navigate(`/profiles/${created.id}`);
      } else {
        if (!profileId) throw new Error("Missing template id");
        await updateProfile(profileId, body);
        setDirty(false);
      }
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!profileId) return;
    if (!confirm(`Delete template "${profileName}"? This cannot be undone.`)) return;
    try {
      await deleteProfile(profileId);
      navigate("/profiles");
    } catch (err) {
      alert(err instanceof Error ? err.message : String(err));
    }
  }

  // ---- inspector-level single-row preview (quick inline) --------------------
  const inspectorPreview = useMemo<string[]>(() => {
    if (!currentRule || !parsedRow) return ["(no sample row)"];
    // Lightweight inline preview for source + static; server preview covers template/computed
    switch (currentRule.kind) {
      case "source": {
        const v = parsedRow[currentRule.column];
        return [v == null ? "" : String(v)];
      }
      case "static":
        return [segmentsToEditing(currentRule.value)];
      default:
        return ["(computed at export)"];
    }
  }, [currentRule, parsedRow]);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <div className="flex h-[calc(100vh-3rem)] min-h-0 flex-col bg-slate-50 dark:bg-background text-slate-900 dark:text-slate-100">
      {/* ---- Header bar -------------------------------------------------- */}
      <div className="flex flex-wrap items-center gap-3 border-b border-slate-200 dark:border-border bg-white dark:bg-surface px-4 py-2.5">
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-3">
          {/* Name input */}
          <input
            type="text"
            value={profileName}
            onChange={(e) => {
              setProfileName(e.target.value);
              setDirty(true);
            }}
            placeholder="Template name"
            className="min-w-[180px] rounded border border-slate-300 dark:border-border px-2 py-1 text-sm font-medium text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-slate-400"
          />
          {/* Product type dropdown */}
          <label className="flex items-center gap-1.5 text-xs text-slate-600 dark:text-slate-400">
            <span className="whitespace-nowrap">Product type</span>
            <select
              value={profile.productType}
              onChange={(e) => {
                const next = { ...profile, productType: e.target.value as Profile["productType"] };
                setProfileAndPreview(next);
                setDirty(true);
              }}
              className="rounded border border-slate-300 dark:border-border bg-white dark:bg-surface px-2 py-1 text-xs"
            >
              {PRODUCT_TYPES.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </label>
          {/* isDefault checkbox */}
          <label className="flex items-center gap-1.5 text-xs text-slate-600 dark:text-slate-400">
            <input
              type="checkbox"
              checked={profile.defaults !== undefined}
              onChange={(e) => {
                setProfile((p) => ({
                  ...p,
                  defaults: e.target.checked ? (p.defaults ?? {}) : undefined,
                }));
                setDirty(true);
              }}
            />
            Has defaults
          </label>
          {dirty && (
            <span className="rounded-full bg-amber-100 dark:bg-amber-950/50 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-amber-800 dark:text-amber-300">
              Unsaved
            </span>
          )}
        </div>

        {/* Right side actions */}
        <div className="flex items-center gap-2">
          {mode === "edit" && profileId && (
            <button
              type="button"
              onClick={handleDelete}
              className="rounded border border-red-200 dark:border-red-900/60 bg-white dark:bg-surface px-3 py-1.5 text-xs font-medium text-red-600 dark:text-red-400 transition hover:bg-red-50 dark:hover:bg-red-950/40"
            >
              Delete
            </button>
          )}
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="rounded bg-slate-900 dark:bg-slate-100 dark:text-slate-950 px-3 py-1.5 text-xs font-medium text-white transition hover:bg-slate-700 dark:hover:bg-slate-200 disabled:opacity-50"
          >
            {saving ? "Saving…" : mode === "new" ? "Create template" : "Save template"}
          </button>
        </div>
      </div>

      {/* Save error banner */}
      {saveError && (
        <div className="border-b border-red-200 dark:border-red-900/60 bg-red-50 dark:bg-red-950/40 px-4 py-2 text-xs text-red-700 dark:text-red-300">
          {saveError}
        </div>
      )}

      {/* ---- 3-pane body ------------------------------------------------- */}
      <div className="flex min-h-0 flex-1">
        {/* ---- Left: Target column groups ---- */}
        <aside className="w-64 shrink-0 overflow-y-auto border-r border-slate-200 dark:border-border bg-white dark:bg-surface">
          <div className="px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Target columns
          </div>
          <div className="space-y-2 px-2 pb-4">
            {COLUMN_GROUPS.map((group) => (
              <div key={group.label}>
                <div className="px-2 pt-2 text-[11px] font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500">
                  {group.label}
                </div>
                <ul className="mt-1 space-y-0.5">
                  {group.columns.map((col) => {
                    const set = !!profile.fields[col];
                    const active = selectedColumn === col;
                    return (
                      <li key={col}>
                        <button
                          type="button"
                          onClick={() => {
                            if (active) return;
                            setSelectedColumn(col);
                            // Auto-create an empty rule if this column has none
                            if (!profile.fields[col]) {
                              setProfile((p) => ({
                                ...p,
                                fields: { ...p.fields, [col]: emptyRule("source") },
                              }));
                            }
                          }}
                          className={[
                            "flex w-full items-center gap-2 rounded px-2 py-1 text-left text-xs transition",
                            active
                              ? "bg-slate-900 dark:bg-slate-100 dark:text-slate-950 text-white"
                              : "text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-surface-2",
                          ].join(" ")}
                        >
                          <span
                            aria-hidden="true"
                            className={[
                              "inline-block h-1.5 w-1.5 shrink-0 rounded-full",
                              set
                                ? "bg-emerald-500"
                                : active
                                  ? "bg-white/40"
                                  : "bg-slate-300 dark:bg-surface-2",
                            ].join(" ")}
                          />
                          <span className="truncate" title={col}>
                            {col}
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>
        </aside>

        {/* ---- Center: sample source row ---- */}
        <section className="flex min-w-0 flex-1 flex-col overflow-hidden">
          <div className="flex items-center justify-between border-b border-slate-200 dark:border-border bg-white dark:bg-surface px-3 py-2">
            <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
              Sample source row
            </div>
            <div className="text-[11px] text-slate-500 dark:text-slate-400">
              Paste a row as JSON, click a key to insert it with{" "}
              <code className="rounded-full bg-slate-100 dark:bg-surface-2 px-1.5 font-mono">/</code>
            </div>
          </div>

          <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto p-3">
            {/* JSON textarea */}
            <div>
              <textarea
                value={sampleJson}
                onChange={(e) => handleSampleChangeFull(e.target.value)}
                rows={6}
                placeholder={`{\n  "Sku Code": "60968",\n  "COLOR": "rani_pink",\n  "FABRIC": "Banarasi Silk",\n  "Transfer Price": 6795\n}`}
                className={[
                  "w-full rounded border px-3 py-2 font-mono text-xs text-slate-800 dark:text-slate-200 focus:outline-none focus:ring-2",
                  sampleParseError
                    ? "border-red-400 focus:ring-red-300"
                    : "border-slate-300 dark:border-border focus:ring-slate-400",
                ].join(" ")}
              />
              {sampleParseError && (
                <p className="mt-1 text-[11px] text-red-600 dark:text-red-400">{sampleParseError}</p>
              )}
            </div>

            {/* Clickable key chips */}
            {sampleHeaders.length > 0 && (
              <div>
                <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500">
                  Click a key to insert at cursor
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {sampleHeaders.map((h) => (
                    <button
                      key={h}
                      type="button"
                      onClick={() => insertAtFocus(`{{${h}}}`)}
                      className="inline-flex max-w-[180px] items-center gap-1 rounded-full bg-slate-50 dark:bg-surface-2 px-2 py-0.5 text-[11px] text-slate-700 dark:text-slate-300 ring-1 ring-slate-200 dark:ring-slate-700 transition hover:bg-slate-100 dark:hover:bg-surface-2"
                      title={`Insert /${h}`}
                    >
                      <span className="text-[9px] font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500">
                        source
                      </span>
                      <span className="truncate font-mono">{h}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Parsed row preview table */}
            {parsedRow && (
              <div className="overflow-x-auto rounded border border-slate-200 dark:border-border bg-white dark:bg-surface">
                <table className="min-w-full border-collapse text-xs">
                  <thead className="bg-slate-100 dark:bg-surface-2">
                    <tr>
                      <th className="px-3 py-1.5 text-left font-semibold text-slate-600 dark:text-slate-400">Key</th>
                      <th className="px-3 py-1.5 text-left font-semibold text-slate-600 dark:text-slate-400">Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sampleHeaders.map((h) => (
                      <tr key={h} className="border-t border-slate-100 dark:border-border">
                        <td className="px-3 py-1 font-mono text-slate-700 dark:text-slate-300">{h}</td>
                        <td className="px-3 py-1 text-slate-800 dark:text-slate-200">
                          {parsedRow[h] == null || parsedRow[h] === "" ? (
                            <span className="text-slate-300 dark:text-slate-600">Blank</span>
                          ) : (
                            String(parsedRow[h])
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </section>

        {/* ---- Right: inspector ---- */}
        <aside className="w-96 shrink-0 overflow-y-auto border-l border-slate-200 dark:border-border bg-white dark:bg-surface">
          {/* Variants panel */}
          <div className="border-b border-slate-200 dark:border-border">
            <button
              type="button"
              onClick={() => setVariantsOpen((o) => !o)}
              className="flex w-full items-center justify-between px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-surface-2"
            >
              <span>Variants</span>
              <span>{variantsOpen ? "−" : "+"}</span>
            </button>
            {variantsOpen && (
              <div className="space-y-2 px-3 pb-3">
                <label className="flex items-center gap-2 text-xs text-slate-700 dark:text-slate-300">
                  <input
                    type="checkbox"
                    checked={variants.enabled}
                    onChange={(e) => updateVariants({ enabled: e.target.checked })}
                  />
                  Enable variants
                </label>
                <label className="block text-xs text-slate-600 dark:text-slate-400">
                  Option name
                  <input
                    type="text"
                    value={variants.optionName ?? ""}
                    onChange={(e) => updateVariants({ optionName: e.target.value })}
                    placeholder="e.g. Stitching"
                    className="mt-1 w-full rounded border border-slate-300 dark:border-border px-2 py-1 text-xs"
                  />
                </label>
                <div className="rounded border border-slate-200 dark:border-border">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50 dark:bg-background text-[11px] uppercase text-slate-500 dark:text-slate-400">
                      <tr>
                        <th className="px-2 py-1 text-left font-semibold">Value</th>
                        <th className="px-2 py-1 text-right font-semibold">Δ Price</th>
                        <th className="px-2 py-1 text-left font-semibold">SKU suffix</th>
                        <th className="px-1 py-1" />
                      </tr>
                    </thead>
                    <tbody>
                      {variantOptions.length === 0 && (
                        <tr>
                          <td colSpan={4} className="px-2 py-2 text-center text-slate-400 dark:text-slate-500">
                            No variant options
                          </td>
                        </tr>
                      )}
                      {variantOptions.map((opt, i) => (
                        <tr key={i} className="border-t border-slate-100 dark:border-border">
                          <td className="px-1 py-1">
                            <input
                              type="text"
                              value={opt.value}
                              onChange={(e) => updateVariantOption(i, { value: e.target.value })}
                              className="w-full rounded border border-slate-200 dark:border-border px-1.5 py-0.5"
                            />
                          </td>
                          <td className="px-1 py-1">
                            <input
                              type="number"
                              value={opt.priceDelta}
                              onChange={(e) =>
                                updateVariantOption(i, { priceDelta: Number(e.target.value) })
                              }
                              className="w-full rounded border border-slate-200 dark:border-border px-1.5 py-0.5 text-right tabular-nums"
                            />
                          </td>
                          <td className="px-1 py-1">
                            <input
                              type="text"
                              value={opt.skuSuffix}
                              onChange={(e) => updateVariantOption(i, { skuSuffix: e.target.value })}
                              className="w-full rounded border border-slate-200 dark:border-border px-1.5 py-0.5"
                            />
                          </td>
                          <td className="px-1 py-1 text-right">
                            <button
                              type="button"
                              onClick={() => removeVariantOption(i)}
                              className="text-slate-400 dark:text-slate-500 hover:text-red-600 dark:hover:text-red-300"
                              aria-label="Remove option"
                            >
                              x
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <button
                  type="button"
                  onClick={addVariantOption}
                  className="rounded border border-dashed border-slate-300 dark:border-border px-2 py-1 text-xs text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-surface-2"
                >
                  + Add option
                </button>
              </div>
            )}
          </div>

          {/* Inspector body */}
          <div className="px-3 py-3">
            {!selectedColumn ? (
              <div className="rounded border border-dashed border-slate-300 dark:border-border bg-slate-50 dark:bg-background px-3 py-8 text-center text-xs text-slate-500 dark:text-slate-400">
                Pick a column on the left to edit its rule.
              </div>
            ) : !currentRule ? (
              <div className="rounded border border-dashed border-slate-300 dark:border-border bg-slate-50 dark:bg-background px-3 py-8 text-center text-xs text-slate-500 dark:text-slate-400">
                This column has no rule yet. Pick a kind below to add one.
              </div>
            ) : (
              <RuleEditor
                column={selectedColumn}
                rule={currentRule}
                sourceHeaders={sampleHeaders}
                onKindChange={changeKind}
                onFieldChange={updateRuleField}
                onClear={clearRule}
                registerInput={registerInput}
                setFocusedInputId={setFocusedInputId}
                insertAtFocus={insertAtFocus}
                previewValues={inspectorPreview}
              />
            )}
          </div>
        </aside>
      </div>

    </div>
  );
}

// ---------------------------------------------------------------------------
// RuleEditor sub-component
// ---------------------------------------------------------------------------

function RuleEditor({
  column,
  rule,
  sourceHeaders,
  onKindChange,
  onFieldChange,
  onClear,
  registerInput,
  setFocusedInputId,
  insertAtFocus,
  previewValues,
}: {
  column: string;
  rule: Rule;
  sourceHeaders: string[];
  onKindChange: (k: RuleKind) => void;
  onFieldChange: (field: string, value: string) => void;
  onClear: () => void;
  registerInput: (id: string) => (el: HTMLInputElement | HTMLTextAreaElement | null) => void;
  setFocusedInputId: (id: string | null) => void;
  insertAtFocus: (text: string) => void;
  previewValues: string[];
}) {
  return (
    <div className="space-y-3">
      {/* Field name + clear */}
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Field
          </div>
          <div className="mt-0.5 text-sm font-semibold text-slate-900 dark:text-slate-100" title={column}>
            {column}
          </div>
        </div>
        <button
          type="button"
          onClick={onClear}
          className="mt-1 rounded border border-slate-200 dark:border-border px-2 py-0.5 text-[11px] text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-surface-2 hover:text-red-600 dark:hover:text-red-300"
        >
          Clear rule
        </button>
      </div>

      {/* Segmented kind picker */}
      <div className="inline-flex rounded border border-slate-200 dark:border-border bg-slate-50 dark:bg-background p-0.5">
        {KINDS.map((k) => {
          const active = rule.kind === k.value;
          return (
            <button
              key={k.value}
              type="button"
              onClick={() => onKindChange(k.value)}
              className={[
                "rounded px-2 py-1 text-xs font-medium transition",
                active
                  ? "bg-white dark:bg-surface text-slate-900 dark:text-slate-100 shadow-sm"
                  : "text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200",
              ].join(" ")}
            >
              {k.label}
            </button>
          );
        })}
      </div>

      {/* source */}
      {rule.kind === "source" && (
        <label className="block text-xs text-slate-600 dark:text-slate-400">
          Source column
          {sourceHeaders.length > 0 ? (
            <select
              value={rule.column}
              onChange={(e) => onFieldChange("column", e.target.value)}
              className="mt-1 w-full rounded border border-slate-300 dark:border-border px-2 py-1 text-xs"
            >
              <option value="">Select</option>
              {sourceHeaders.map((h) => (
                <option key={h} value={h}>
                  {h}
                </option>
              ))}
            </select>
          ) : (
            <input
              type="text"
              value={rule.column}
              onChange={(e) => onFieldChange("column", e.target.value)}
              placeholder="Exact header name"
              className="mt-1 w-full rounded border border-slate-300 dark:border-border px-2 py-1 text-xs"
            />
          )}
        </label>
      )}

      {/* static */}
      {rule.kind === "static" && (
        <label className="block text-xs text-slate-600 dark:text-slate-400">
          Static value
          <input
            type="text"
            value={segmentsToEditing(rule.value)}
            onChange={(e) => onFieldChange("value", e.target.value)}
            className="mt-1 w-full rounded border border-slate-300 dark:border-border px-2 py-1 text-xs"
            ref={registerInput(`${column}::value`)}
            onFocus={() => setFocusedInputId(`${column}::value`)}
            onBlur={() => setFocusedInputId(null)}
          />
        </label>
      )}

      {/* template */}
      {rule.kind === "template" && (
        <div className="space-y-2">
          {/* Helper chips */}
          <div className="flex flex-wrap gap-1">
            {TEMPLATE_HELPER_CHIPS.map((chip) => (
              <button
                key={chip}
                type="button"
                onClick={() => insertAtFocus(` | ${chip}`)}
                className="rounded-full border border-slate-200 dark:border-border bg-slate-50 dark:bg-background px-2 py-0.5 text-[11px] text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-surface-2"
                title={`Insert | ${chip}`}
              >
                {chip}
              </button>
            ))}
          </div>
          <label className="block text-xs text-slate-600 dark:text-slate-400">
            Template
            <textarea
              value={segmentsToEditing(rule.value)}
              onChange={(e) => onFieldChange("value", e.target.value)}
              rows={4}
              className="mt-1 w-full rounded border border-slate-300 dark:border-border px-2 py-1 font-mono text-xs"
              ref={registerInput(`${column}::value`)}
              onFocus={() => setFocusedInputId(`${column}::value`)}
              onBlur={() => setFocusedInputId(null)}
              placeholder="/Product Tittle"
            />
          </label>
        </div>
      )}

      {/* computed */}
      {rule.kind === "computed" && (
        <div className="space-y-2">
          <label className="block text-xs text-slate-600 dark:text-slate-400">
            Function
            <select
              value={rule.fn}
              onChange={(e) => onFieldChange("fn", e.target.value)}
              className="mt-1 w-full rounded border border-slate-300 dark:border-border px-2 py-1 text-xs"
            >
              {COMPUTED_FNS.map((fn) => (
                <option key={fn} value={fn}>
                  {fn}
                </option>
              ))}
            </select>
          </label>
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <span className="text-xs text-slate-600 dark:text-slate-400">Args</span>
              <button
                type="button"
                onClick={() => {
                  const args = [...(rule.args ?? []), ""];
                  onFieldChange(`arg:${args.length - 1}`, "");
                }}
                className="rounded border border-dashed border-slate-300 dark:border-border px-2 py-0.5 text-[11px] text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-surface-2"
              >
                + Add arg
              </button>
            </div>
            {(rule.args ?? []).length === 0 && (
              <div className="text-[11px] text-slate-400 dark:text-slate-500">No args.</div>
            )}
            {(rule.args ?? []).map((arg, i) => (
              <input
                key={i}
                type="text"
                value={String(arg)}
                onChange={(e) => onFieldChange(`arg:${i}`, e.target.value)}
                className="w-full rounded border border-slate-300 dark:border-border px-2 py-1 font-mono text-xs"
                placeholder={`arg ${i + 1}`}
                ref={registerInput(`${column}::arg:${i}`)}
                onFocus={() => setFocusedInputId(`${column}::arg:${i}`)}
                onBlur={() => setFocusedInputId(null)}
              />
            ))}
          </div>
        </div>
      )}

      {/* Inline preview */}
      <div className="pt-2">
        <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
          Preview
        </div>
        <ul className="mt-1 space-y-0.5 rounded border border-slate-200 dark:border-border bg-slate-50 dark:bg-background p-2 font-mono text-[11px]">
          {previewValues.map((v, i) => (
            <li key={i} className="flex gap-2">
              <span className="text-slate-400 dark:text-slate-500">{i + 1}</span>
              <span className="truncate text-slate-800 dark:text-slate-200">
                {v === "" ? <span className="text-slate-300 dark:text-slate-600">Blank</span> : v}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
